import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import dayjs from 'dayjs';
import type { EChartsOption } from 'echarts';
import {
  Observable,
  catchError,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  toArray,
} from 'rxjs';

import { BaseViewModel } from '@app/shared/view-model/base-view-model';
import { ListRecordsService } from '@app/data/record/list-records.service';
import { GetRecordService } from '@app/data/record/get-record.service';
import { DatabaseCacheService } from '@app/data/cache/database-cache.service';
import { RecordData, TypesUtils } from '../../../types';
import { geohashToLatLng } from '@app/shared/utils/geohash.utils';
import { TOMASS_SUMMARY_I18N } from './tomass-summary.i18n';
import { TomassSummaryCacheService } from './tomass-summary-cache.service';
import {
  type ChartEntry,
  type ChartLegendItem,
  type FrequencyRow,
  type MapMarkerData,
  type ScoreLevel,
  type TomassFilterState,
  type TomassKpi,
  type TomassSummaryState,
  type UserOption,
  TOMASS_CHART_COLORS,
} from './tomass-summary.types';

const TOMASS_RECORD_TYPES = new Set(['TOMASS', 'TOMASS_DATA']);
const SCORE_LEVELS: ('A' | 'B' | 'C')[] = ['A', 'B', 'C'];
const REGION_ESTIMATE_ANCHORS = [
  { prefecture: '北海道', city: '札幌市', lat: 43.0618, lng: 141.3545 },
  { prefecture: '宮城県', city: '仙台市', lat: 38.2682, lng: 140.8694 },
  { prefecture: '新潟県', city: '新潟市', lat: 37.9161, lng: 139.0364 },
  { prefecture: '茨城県', city: '水戸市', lat: 36.3659, lng: 140.4712 },
  { prefecture: '栃木県', city: '宇都宮市', lat: 36.5551, lng: 139.8828 },
  { prefecture: '群馬県', city: '前橋市', lat: 36.3911, lng: 139.0608 },
  { prefecture: '埼玉県', city: 'さいたま市', lat: 35.8617, lng: 139.6455 },
  { prefecture: '千葉県', city: '千葉市', lat: 35.6074, lng: 140.1065 },
  { prefecture: '東京都', city: '千代田区', lat: 35.6896, lng: 139.6917 },
  { prefecture: '神奈川県', city: '横浜市', lat: 35.4437, lng: 139.6380 },
  { prefecture: '静岡県', city: '静岡市', lat: 34.9769, lng: 138.3831 },
  { prefecture: '愛知県', city: '名古屋市', lat: 35.1815, lng: 136.9066 },
  { prefecture: '京都府', city: '京都市', lat: 35.0116, lng: 135.7681 },
  { prefecture: '大阪府', city: '大阪市', lat: 34.6937, lng: 135.5023 },
  { prefecture: '兵庫県', city: '神戸市', lat: 34.6901, lng: 135.1955 },
  { prefecture: '岡山県', city: '岡山市', lat: 34.6551, lng: 133.9195 },
  { prefecture: '広島県', city: '広島市', lat: 34.3853, lng: 132.4553 },
  { prefecture: '香川県', city: '高松市', lat: 34.3428, lng: 134.0466 },
  { prefecture: '愛媛県', city: '松山市', lat: 33.8392, lng: 132.7657 },
  { prefecture: '福岡県', city: '福岡市', lat: 33.5902, lng: 130.4017 },
  { prefecture: '熊本県', city: '熊本市', lat: 32.8031, lng: 130.7079 },
  { prefecture: '鹿児島県', city: '鹿児島市', lat: 31.5966, lng: 130.5571 },
  { prefecture: '沖縄県', city: '那覇市', lat: 26.2124, lng: 127.6809 },
] as const;

const initialState: TomassSummaryState = {
  status: 'idle',
  errorMessage: null,
  hasLoaded: false,
  hasAggregated: false,
  filters: {
    startDate: dayjs().subtract(1, 'month').startOf('day').toDate(),
    endDate: dayjs().endOf('day').toDate(),
    selectedUserIds: [],
  },
  userOptions: [],
  kpi: { recordCount: 0, userCount: 0, avgAgeGroup: null, avgPst: null, avgGpst: null },
  frequencyRows: [],
  mapMarkers: [],
  ageChartOption: {},
  ageChartLegend: [],
  genderChartOption: {},
  genderChartLegend: [],
  crackerRatioOption: {},
  crackerRatioLegend: [],
  crackerHistogramOption: {},
};

@Injectable()
export class TomassSummaryViewModel extends BaseViewModel<TomassSummaryState> {
  private readonly listRecordsService = inject(ListRecordsService);
  private readonly getRecordService = inject(GetRecordService);
  private readonly common = inject(DatabaseCacheService);
  private readonly summaryCache = inject(TomassSummaryCacheService);

  private allRecords: RecordData[] = [];
  private currentSectionId: string | null = null;
  private readonly recordWithDataCache = new Map<string, RecordData>();
  private readonly selectionInitializedSections = new Set<string>();
  private latestFetchRequestId = 0;

  private readonly i18n = TOMASS_SUMMARY_I18N;
  private readonly unknownLabel = this.i18n.label.unknown;
  private readonly underTenLabel = this.i18n.label.underTen;
  private readonly overHundredLabel = this.i18n.label.overHundred;

  constructor() {
    super(initialState);
  }

  // ── Public API ──────────────────────────────────────────

  init(destroyRef: DestroyRef, routeSectionId$: Observable<string | null>): void {
    this.syncSection();

    this.common.parametersUpdated
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => this.syncSection());

    routeSectionId$
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => this.syncSection());
  }

  updateFilters(filters: Partial<TomassFilterState>): void {
    const merged = { ...this.currentState.filters, ...filters };
    const normalized = this.normalizeDateRange(merged);
    if (filters.selectedUserIds && this.currentSectionId) {
      this.selectionInitializedSections.add(this.currentSectionId);
    }
    this.patchState({ filters: normalized });
  }

  resetDateRange(): void {
    this.updateFilters({
      startDate: dayjs().subtract(1, 'month').startOf('day').toDate(),
      endDate: dayjs().endOf('day').toDate(),
    });
  }

  /** 集計ボタンから呼ばれる。初回は取得し、以降はローカルキャッシュを優先して再集計する */
  aggregate(): void {
    if (!this.currentSectionId || this.currentState.filters.selectedUserIds.length === 0) {
      return;
    }

    this.patchState({ hasAggregated: true });
    this.fetchRecords(this.currentSectionId);
  }

  /** セクション変更後の初回取得、または明示的な再取得 */
  refetch(): void {
    if (this.currentSectionId) {
      this.summaryCache.clear(this.currentSectionId);
      this.recordWithDataCache.clear();
      this.allRecords = [];
      this.fetchRecords(this.currentSectionId);
    }
  }

  formatDateLabel(date: Date | null): string {
    return date ? dayjs(date).format('YYYY/MM/DD') : '-';
  }

  get dateRangeLabel(): string {
    const { startDate, endDate } = this.currentState.filters;
    return `${this.formatDateLabel(startDate)} ~ ${this.formatDateLabel(endDate)}`;
  }

  // ── Section sync ────────────────────────────────────────

  private syncSection(): void {
    const selectedSection = this.common.getSelectedSection();
    const sectionId = selectedSection?.sectionId ?? null;

    if (!sectionId) {
      return;
    }

    const userOptions = this.buildUserOptionsFromCache();

    if (sectionId !== this.currentSectionId) {
      // Invalidate in-flight requests from the previous section.
      this.latestFetchRequestId++;
      this.recordWithDataCache.clear();
      this.allRecords = [];
      this.currentSectionId = sectionId;

      const cachedDetailed = this.summaryCache.getDetailedRecords(sectionId);
      const hasCachedDetailed = cachedDetailed !== undefined;
      if (cachedDetailed) {
        this.restoreRecords(cachedDetailed);
      }

      const defaultSelectedUserIds = this.getDefaultSelectedUserIds(userOptions);
      if (defaultSelectedUserIds.length > 0) {
        this.selectionInitializedSections.add(sectionId);
      } else {
        this.selectionInitializedSections.delete(sectionId);
      }

      this.patchState({
        status: hasCachedDetailed ? 'success' : 'idle',
        errorMessage: null,
        hasLoaded: hasCachedDetailed,
        hasAggregated: false,
        filters: { ...this.currentState.filters, selectedUserIds: defaultSelectedUserIds },
        userOptions,
        kpi: { ...initialState.kpi },
        frequencyRows: [],
        mapMarkers: [],
        ageChartOption: {},
        ageChartLegend: [],
        genderChartOption: {},
        genderChartLegend: [],
        crackerRatioOption: {},
        crackerRatioLegend: [],
        crackerHistogramOption: {},
      });
      return;
    }

    const validIds = new Set(userOptions.map((o) => o.userId));
    let selectedUserIds = this.currentState.filters.selectedUserIds.filter((id) =>
      validIds.has(id),
    );
    if (
      selectedUserIds.length === 0
      && userOptions.length > 0
      && !this.selectionInitializedSections.has(sectionId)
    ) {
      selectedUserIds = this.getDefaultSelectedUserIds(userOptions);
      this.selectionInitializedSections.add(sectionId);
    }
    const shouldPatchUserOptions = !this.areUserOptionsEqual(this.currentState.userOptions, userOptions);
    const shouldPatchSelectedIds = !this.areStringArraysEqual(this.currentState.filters.selectedUserIds, selectedUserIds);

    if (shouldPatchUserOptions || shouldPatchSelectedIds) {
      this.patchState({
        userOptions,
        filters: { ...this.currentState.filters, selectedUserIds },
      });
    }
  }

  // ── Data fetching ───────────────────────────────────────

  private fetchRecords(sectionId: string): void {
    const requestId = ++this.latestFetchRequestId;
    this.setLoading();

    this.getOrFetchListRecords(sectionId)
      .pipe(
        switchMap((listRecords) => {
          const rangeRecords = this.filterByCreatedAt(listRecords, this.currentState.filters);
          const targetRecords = this.applyUserFilterIfAvailable(
            rangeRecords,
            this.currentState.filters.selectedUserIds,
          );
          const missingRecords = targetRecords.filter((r) => !this.recordWithDataCache.has(r.id));

          if (missingRecords.length === 0) {
            return of(listRecords);
          }

          return this.enrichRecords(sectionId, missingRecords).pipe(map(() => listRecords));
        }),
      )
      .subscribe({
        next: (listRecords) => {
          if (requestId !== this.latestFetchRequestId) return;
          this.syncDetailedRecordsFromCache(listRecords);
          this.summaryCache.setDetailedRecords(sectionId, this.allRecords);

          const userOptions = this.buildUserOptionsFromCache();
          const validIds = new Set(userOptions.map((o) => o.userId));
          const effectiveIds = this.currentState.filters.selectedUserIds.filter((id) =>
            validIds.has(id),
          );

          this.patchState({
            hasLoaded: true,
            filters: { ...this.currentState.filters, selectedUserIds: effectiveIds },
            userOptions,
          });
          this.rebuildDerivedState();
          this.setSuccess();
        },
        error: () => {
          if (requestId !== this.latestFetchRequestId) return;
          this.allRecords = [];
          this.patchState({ userOptions: this.buildUserOptionsFromCache(), hasLoaded: true });
          this.rebuildDerivedState();
          this.setError(this.i18n.error.fetchFailed);
        },
      });
  }

  private enrichRecords(
    sectionId: string,
    records: RecordData[],
  ): Observable<RecordData[]> {
    if (records.length === 0) return of([]);

    return from(records).pipe(
      mergeMap((record) => this.fetchRecordWithData(record, sectionId), 8),
      toArray(),
      map((enriched) => {
        const byId = new Map(records.map((r) => [r.id, r]));
        enriched.forEach((r) => byId.set(r.id, r));
        return records.map((r) => byId.get(r.id) ?? r);
      }),
    );
  }

  private fetchRecordWithData(
    fallback: RecordData,
    sectionId: string,
  ): Observable<RecordData> {
    const cached = this.recordWithDataCache.get(fallback.id);
    if (cached) return of(cached);

    return this.getRecordService.getRecord(fallback.id, sectionId).pipe(
      map((raw) => {
        const converted = TypesUtils.convertJsonToRecords([raw], false);
        const record = converted.records[0] ?? fallback;
        this.recordWithDataCache.set(record.id, record);
        return record;
      }),
      catchError(() => of(fallback)),
    );
  }

  private restoreRecords(records: RecordData[]): void {
    this.allRecords = records;
    this.recordWithDataCache.clear();
    for (const record of records) {
      this.recordWithDataCache.set(record.id, record);
    }
  }

  private getOrFetchListRecords(sectionId: string): Observable<RecordData[]> {
    const cached = this.summaryCache.getListRecords(sectionId);
    if (cached !== undefined) {
      return of(cached);
    }

    return this.fetchListRecords(sectionId).pipe(
      map((records) => {
        this.summaryCache.setListRecords(sectionId, records);
        return records;
      }),
    );
  }

  private fetchListRecords(sectionId: string): Observable<RecordData[]> {
    return forkJoin([
      this.listRecordsByRecordType(sectionId, 'TOMASS'),
      this.listRecordsByRecordType(sectionId, 'TOMASS_DATA'),
    ]).pipe(map(([tomass, tomassData]) => this.mergeListRecordsById([...tomass, ...tomassData])));
  }

  private listRecordsByRecordType(
    sectionId: string,
    recordType: 'TOMASS' | 'TOMASS_DATA',
  ): Observable<RecordData[]> {
    return this.listRecordsService
      .listRecords(sectionId, '', null, recordType, null)
      .pipe(
        map((response) => {
          const rawRecords = Array.isArray(response) ? response : [];
          const converted = TypesUtils.convertJsonToRecords(rawRecords, false);
          return converted.records.filter((record) =>
            TOMASS_RECORD_TYPES.has(record?.data?.recordType),
          );
        }),
      );
  }

  private mergeListRecordsById(records: RecordData[]): RecordData[] {
    const mapById = new Map<string, RecordData>();
    for (const record of records) {
      if (!record?.id) {
        continue;
      }
      mapById.set(record.id, record);
    }

    return Array.from(mapById.values()).sort((a, b) => {
      const aTime = dayjs(a?.data?.createdAt).valueOf();
      const bTime = dayjs(b?.data?.createdAt).valueOf();
      if (!Number.isFinite(aTime) && !Number.isFinite(bTime)) {
        return 0;
      }
      if (!Number.isFinite(aTime)) {
        return 1;
      }
      if (!Number.isFinite(bTime)) {
        return -1;
      }
      return bTime - aTime;
    });
  }

  private filterByCreatedAt(records: RecordData[], filters: TomassFilterState): RecordData[] {
    const start = filters.startDate ? dayjs(filters.startDate).startOf('day') : null;
    const end = filters.endDate ? dayjs(filters.endDate).endOf('day') : null;

    return records.filter((record) => {
      const createdAt = record?.data?.createdAt;
      if (!createdAt) {
        return false;
      }
      const createdAtDayjs = dayjs(createdAt);
      if (!createdAtDayjs.isValid()) {
        return false;
      }
      if (start && createdAtDayjs.isBefore(start)) {
        return false;
      }
      if (end && createdAtDayjs.isAfter(end)) {
        return false;
      }
      return true;
    });
  }

  private applyUserFilterIfAvailable(records: RecordData[], userIds: string[]): RecordData[] {
    if (userIds.length === 0) {
      return records;
    }

    const selectedIds = new Set(userIds);
    return records.filter((record) => {
      const userId = record?.meta?.user?.userId;
      if (!userId) {
        return true;
      }
      return selectedIds.has(userId);
    });
  }

  private syncDetailedRecordsFromCache(listRecords: RecordData[]): void {
    const detailedRecords = listRecords
      .map((record) => this.recordWithDataCache.get(record.id))
      .filter((record): record is RecordData => !!record);

    this.allRecords = detailedRecords;
  }

  // ── Derived state ───────────────────────────────────────

  private rebuildDerivedState(): void {
    const filtered = this.getFilteredRecords();
    const kpi = this.buildKpi(filtered);
    const frequencyRows = this.buildFrequencyRows(filtered);
    const mapMarkers = this.buildMapMarkers(filtered);

    const ageEntries = this.sortByAgeGroupDesc(
      this.countBy(filtered, (r) => this.getAgeGroupLabel(r)),
    );
    const genderEntries = this.sortByCustomOrder(
      this.countBy(filtered, (r) => this.getGenderLabel(r)),
      [this.i18n.label.male, this.i18n.label.female, this.i18n.label.other],
    );
    const crackerEntries = this.sortByCustomOrder(
      this.countBy(filtered, (r) => this.getScoreLabel(r)),
      [...SCORE_LEVELS, this.unknownLabel],
    );

    this.patchState({
      kpi,
      frequencyRows,
      mapMarkers,
      ageChartOption: this.buildPieOption(ageEntries, { color: TOMASS_CHART_COLORS.age }),
      ageChartLegend: this.buildLegendItems(ageEntries, TOMASS_CHART_COLORS.age),
      genderChartOption: this.buildPieOption(genderEntries, { color: TOMASS_CHART_COLORS.gender }),
      genderChartLegend: this.buildLegendItems(genderEntries, TOMASS_CHART_COLORS.gender),
      crackerRatioOption: this.buildPieOption(crackerEntries, { color: TOMASS_CHART_COLORS.cracker }),
      crackerRatioLegend: this.buildLegendItems(crackerEntries, TOMASS_CHART_COLORS.cracker),
      crackerHistogramOption: this.buildCrackerHistogram(filtered),
    });
  }

  // ── KPI ─────────────────────────────────────────────────

  private buildKpi(records: RecordData[]): TomassKpi {
    const userIds = new Set<string>();
    const ageValues: number[] = [];
    let pstSum = 0;
    let pstCount = 0;
    let gpstSum = 0;
    let gpstCount = 0;

    const PST_MAX = 300;

    for (const r of records) {
      const userId = r?.meta?.user?.userId;
      if (typeof userId === 'string' && userId.length > 0) {
        userIds.add(userId);
      }

      const age = this.extractAgeRepresentative(r);
      if (age !== null) {
        ageValues.push(age);
      }

      const pst = Number(r?.data?.pst);
      if (Number.isFinite(pst) && pst > 0 && pst <= PST_MAX) {
        pstSum += pst;
        pstCount++;
      }

      const gpst = Number(r?.data?.gpst);
      if (Number.isFinite(gpst) && gpst > 0 && gpst <= PST_MAX) {
        gpstSum += gpst;
        gpstCount++;
      }
    }

    const medianAge = this.getMedianAge(ageValues);
    const avgAgeGroup = medianAge !== null ? this.toAgeGroupLabel(medianAge) : null;

    return {
      recordCount: records.length,
      userCount: userIds.size,
      avgAgeGroup,
      avgPst: pstCount > 0 ? Math.round((pstSum / pstCount) * 100) / 100 : null,
      avgGpst: gpstCount > 0 ? Math.round((gpstSum / gpstCount) * 100) / 100 : null,
    };
  }

  // ── Frequency table ─────────────────────────────────────

  private buildFrequencyRows(records: RecordData[]): FrequencyRow[] {
    const grouped = new Map<
      string,
      {
        measurementCount: number;
        users: Set<string>;
        geohashes: Set<string>;
        estimatedRegions: Set<string>;
        scoreA: number;
        scoreB: number;
        scoreC: number;
        scoreUnknown: number;
      }
    >();

    for (const r of records) {
      const d = dayjs(r?.data?.createdAt);
      if (!d.isValid()) continue;

      const key = d.format('YYYY-MM-DD');
      const entry = grouped.get(key) ?? {
        measurementCount: 0,
        users: new Set<string>(),
        geohashes: new Set<string>(),
        estimatedRegions: new Set<string>(),
        scoreA: 0,
        scoreB: 0,
        scoreC: 0,
        scoreUnknown: 0,
      };
      entry.measurementCount++;
      entry.users.add(this.getUserDisplayName(r));
      const geohash = this.normalizeGeohash(r?.data?.geolocation);
      if (geohash) {
        entry.geohashes.add(geohash);
      }
      const estimatedRegion = this.estimateRegionLabel(r);
      if (estimatedRegion) {
        entry.estimatedRegions.add(estimatedRegion);
      }
      const score = this.normalizeScore(r);
      if (score === 'A') {
        entry.scoreA += 1;
      } else if (score === 'B') {
        entry.scoreB += 1;
      } else if (score === 'C') {
        entry.scoreC += 1;
      } else {
        entry.scoreUnknown += 1;
      }
      grouped.set(key, entry);
    }

    return Array.from(grouped.entries())
      .map(([key, value]) => {
        return {
          dateKey: key,
          dateLabel: dayjs(key).format('YYYY/MM/DD'),
          measurementCount: value.measurementCount,
          accountCount: value.users.size,
          accountNames: Array.from(value.users).sort((a, b) => a.localeCompare(b, 'ja')),
          geohashes: Array.from(value.geohashes).sort((a, b) => a.localeCompare(b, 'ja')),
          estimatedRegions: Array.from(value.estimatedRegions).sort((a, b) =>
            a.localeCompare(b, 'ja'),
          ),
          scoreA: value.scoreA,
          scoreB: value.scoreB,
          scoreC: value.scoreC,
          scoreUnknown: value.scoreUnknown,
        };
      })
      .sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  }

  private estimateRegionLabel(record: RecordData): string | null {
    const geohash = this.normalizeGeohash(record?.data?.geolocation);
    if (!geohash) {
      return null;
    }

    const point = geohashToLatLng(geohash);
    if (!point) {
      return null;
    }

    const nearest = this.findNearestRegionAnchor(point.lat, point.lng);
    if (!nearest) {
      return `${point.lat.toFixed(2)}, ${point.lng.toFixed(2)}付近`;
    }

    if (nearest.distanceKm <= 400) {
      return `${nearest.prefecture}周辺`;
    }

    return `${point.lat.toFixed(2)}, ${point.lng.toFixed(2)}付近`;
  }

  private findNearestRegionAnchor(
    lat: number,
    lng: number,
  ): { prefecture: string; city: string; distanceKm: number } | null {
    let nearest: { prefecture: string; city: string; distanceKm: number } | null = null;

    for (const anchor of REGION_ESTIMATE_ANCHORS) {
      const distanceKm = this.calculateDistanceKm(lat, lng, anchor.lat, anchor.lng);
      if (!Number.isFinite(distanceKm)) {
        continue;
      }
      if (!nearest || distanceKm < nearest.distanceKm) {
        nearest = {
          prefecture: anchor.prefecture,
          city: anchor.city,
          distanceKm,
        };
      }
    }

    return nearest;
  }

  private calculateDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const toRad = (degree: number): number => degree * (Math.PI / 180);
    const earthRadiusKm = 6371;

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return earthRadiusKm * c;
  }

  // ── Map markers ─────────────────────────────────────────

  private buildMapMarkers(records: RecordData[]): MapMarkerData[] {
    const grouped = new Map<
      string,
      {
        geohash: string;
        lat: number;
        lng: number;
        totalCount: number;
        users: Set<string>;
        scoreA: number;
        scoreB: number;
        scoreC: number;
        scoreUnknown: number;
        ageSum: number;
        ageCount: number;
        seniorCount: number;
        latestCreatedAt: string;
      }
    >();

    for (const r of records) {
      const geohash = this.normalizeGeohash(r?.data?.geolocation);
      if (!geohash) continue;

      const point = geohashToLatLng(geohash);
      if (!point) continue;

      const entry = grouped.get(geohash) ?? {
        geohash,
        lat: point.lat,
        lng: point.lng,
        totalCount: 0,
        users: new Set<string>(),
        scoreA: 0,
        scoreB: 0,
        scoreC: 0,
        scoreUnknown: 0,
        ageSum: 0,
        ageCount: 0,
        seniorCount: 0,
        latestCreatedAt: '',
      };

      entry.totalCount += 1;
      entry.users.add(this.getUserDisplayName(r));

      const score = this.normalizeScore(r);
      if (score === 'A') {
        entry.scoreA += 1;
      } else if (score === 'B') {
        entry.scoreB += 1;
      } else if (score === 'C') {
        entry.scoreC += 1;
      } else {
        entry.scoreUnknown += 1;
      }

      const age = this.extractAgeRepresentative(r);
      if (age !== null) {
        entry.ageSum += age;
        entry.ageCount += 1;
        if (age >= 70) {
          entry.seniorCount += 1;
        }
      }

      const createdAt = r?.data?.createdAt;
      if (createdAt && dayjs(createdAt).isValid()) {
        if (!entry.latestCreatedAt || dayjs(createdAt).isAfter(dayjs(entry.latestCreatedAt))) {
          entry.latestCreatedAt = createdAt;
        }
      }

      grouped.set(geohash, entry);
    }

    return Array.from(grouped.values())
      .map((entry) => {
        const denominator = entry.totalCount > 0 ? entry.totalCount : 1;
        const avgAge = entry.ageCount > 0 ? entry.ageSum / entry.ageCount : null;
        return {
          geohash: entry.geohash,
          lat: entry.lat,
          lng: entry.lng,
          totalCount: entry.totalCount,
          uniqueUserCount: entry.users.size,
          scoreA: entry.scoreA,
          scoreB: entry.scoreB,
          scoreC: entry.scoreC,
          scoreUnknown: entry.scoreUnknown,
          scoreARatio: this.toPercent(entry.scoreA, denominator),
          scoreBRatio: this.toPercent(entry.scoreB, denominator),
          scoreCRatio: this.toPercent(entry.scoreC, denominator),
          seniorRatio: this.toPercent(entry.seniorCount, entry.ageCount || denominator),
          avgAgeGroup: avgAge !== null ? this.toAgeGroupLabel(avgAge) : this.unknownLabel,
          latestCreatedAt: entry.latestCreatedAt,
        };
      })
      .sort((a, b) => b.totalCount - a.totalCount);
  }

  private normalizeGeohash(raw: unknown): string | null {
    if (typeof raw !== 'string') {
      return null;
    }

    const normalized = raw.trim().toLowerCase();
    if (!normalized) {
      return null;
    }

    // geohash base32 only, expected practical precision for map cells.
    if (!/^[0-9bcdefghjkmnpqrstuvwxyz]{4,12}$/.test(normalized)) {
      return null;
    }

    // Numeric-only values are likely non-geohash IDs and can create invalid cells.
    if (!/[bcdefghjkmnpqrstuvwxyz]/.test(normalized)) {
      return null;
    }

    return normalized;
  }

  // ── Filtering ───────────────────────────────────────────

  private getFilteredRecords(): RecordData[] {
    return this.filterRecords(this.allRecords, this.currentState.filters);
  }

  private filterRecords(records: RecordData[], filters: TomassFilterState): RecordData[] {
    const start = filters.startDate ? dayjs(filters.startDate).startOf('day') : null;
    const end = filters.endDate ? dayjs(filters.endDate).endOf('day') : null;
    const userIdSet =
      filters.selectedUserIds.length > 0 ? new Set(filters.selectedUserIds) : null;

    return records.filter((r) => {
      const createdAt = r?.data?.createdAt;
      if (!createdAt) return false;

      const d = dayjs(createdAt);
      if (!d.isValid()) return false;
      if (start && d.isBefore(start)) return false;
      if (end && d.isAfter(end)) return false;
      if (userIdSet && !userIdSet.has(r?.meta?.user?.userId)) return false;
      return true;
    });
  }

  // ── User options ────────────────────────────────────────

  private buildUserOptionsFromCache(): UserOption[] {
    const usersList = this.common.getUsersList() as
      | {
        user?: {
          userId?: string;
          code?: string;
          lastName?: string;
          firstName?: string;
          userName?: string;
        };
      }[]
      | null
      | undefined;

    if (!Array.isArray(usersList)) {
      return [];
    }

    const uniqueUsers = new Map<string, UserOption>();
    for (const entry of usersList) {
      const user = entry?.user;
      const userId = typeof user?.userId === 'string' ? user.userId.trim() : '';
      if (!userId) continue;

      const code = typeof user?.code === 'string' ? user.code.trim() : '';
      const lastName = typeof user?.lastName === 'string' ? user.lastName.trim() : '';
      const firstName = typeof user?.firstName === 'string' ? user.firstName.trim() : '';
      const name = `${lastName} ${firstName}`.trim();
      const userName = typeof user?.userName === 'string' ? user.userName.trim() : '';
      const displayName = code || name || userName || userId;

      uniqueUsers.set(userId, {
        userId,
        displayName,
        code,
        name,
      });
    }

    return Array.from(uniqueUsers.values()).sort((a, b) => {
      const keyA = `${a.code} ${a.name} ${a.displayName}`.trim();
      const keyB = `${b.code} ${b.name} ${b.displayName}`.trim();
      return keyA.localeCompare(keyB, 'ja');
    });
  }

  private getDefaultSelectedUserIds(userOptions: UserOption[]): string[] {
    return userOptions.map((option) => option.userId);
  }

  // ── Chart builders ──────────────────────────────────────

  private buildPieOption(
    entries: ChartEntry[],
    config?: { color?: readonly string[] },
  ): EChartsOption {
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c}件 ({d}%)' },
      legend: { show: false },
      series: [
        {
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          label: { show: false },
          labelLine: { show: false },
          emphasis: {
            itemStyle: {
              shadowBlur: 14,
              shadowOffsetX: 0,
              shadowColor: 'rgba(15, 23, 42, 0.2)',
            },
          },
          data: entries.map(([name, value]) => ({ name, value })),
        },
      ],
      color: config?.color ? [...config.color] : [...TOMASS_CHART_COLORS.age],
    };
  }

  private buildLegendItems(
    entries: ChartEntry[],
    colors: readonly string[],
  ): ChartLegendItem[] {
    const total = entries.reduce((sum, [, v]) => sum + v, 0);
    return entries.map(([label, count], i) => ({
      label,
      count,
      percentage: total > 0 ? ((count / total) * 100).toFixed(1) : '0.0',
      color: colors[i % colors.length],
    }));
  }

  private buildCrackerHistogram(records: RecordData[]): EChartsOption {
    const countsByAge = new Map<string, { A: number; B: number; C: number }>();

    for (const r of records) {
      const ageLabel = this.getAgeGroupLabel(r);
      const score = this.normalizeScore(r);
      const current = countsByAge.get(ageLabel) ?? { A: 0, B: 0, C: 0 };
      if (score === 'A' || score === 'B' || score === 'C') {
        current[score] += 1;
      }
      countsByAge.set(ageLabel, current);
    }

    const ageLabels = Array.from(countsByAge.keys()).sort(
      (a, b) => this.ageGroupSortKey(a) - this.ageGroupSortKey(b),
    );

    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { top: 0, right: 8, data: [...SCORE_LEVELS] },
      grid: { left: 14, right: 16, top: 44, bottom: 20, containLabel: true },
      xAxis: {
        type: 'category',
        data: ageLabels,
        axisLabel: { interval: 0, rotate: ageLabels.length > 8 ? 30 : 0 },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        splitLine: { lineStyle: { type: 'dashed' } },
      },
      series: SCORE_LEVELS.map((level) => ({
        name: level,
        type: 'bar' as const,
        stack: 'score',
        barMaxWidth: 34,
        data: ageLabels.map((age) => countsByAge.get(age)?.[level] ?? 0),
        itemStyle: {
          color:
            level === 'A'
              ? TOMASS_CHART_COLORS.scoreA
              : level === 'B'
                ? TOMASS_CHART_COLORS.scoreB
                : TOMASS_CHART_COLORS.scoreC,
        },
      })),
      media: [
        {
          query: { maxWidth: 920 },
          option: {
            grid: { left: 8, right: 8, top: 48, bottom: 44 },
            xAxis: { axisLabel: { interval: 'auto', rotate: 35, fontSize: 10 } },
            series: [{ barMaxWidth: 26 }, { barMaxWidth: 26 }, { barMaxWidth: 26 }],
          },
        },
        {
          query: { maxWidth: 640 },
          option: {
            legend: { top: 0, left: 'center', right: 'auto' },
            grid: { left: 6, right: 6, top: 54, bottom: 52 },
            xAxis: { axisLabel: { interval: 'auto', rotate: 45, fontSize: 10 } },
            series: [{ barMaxWidth: 20 }, { barMaxWidth: 20 }, { barMaxWidth: 20 }],
          },
        },
      ],
    };
  }

  // ── Helpers ─────────────────────────────────────────────

  private getUserDisplayName(record: RecordData): string {
    const displayName = record?.meta?.user?.displayName?.trim();
    if (displayName) return displayName;
    const code = record?.meta?.user?.code?.trim();
    if (code) return code;
    const lastName = record?.meta?.user?.lastName?.trim() ?? '';
    const firstName = record?.meta?.user?.firstName?.trim() ?? '';
    const fullName = `${lastName} ${firstName}`.trim();
    if (fullName) return fullName;
    return this.unknownLabel;
  }

  private areUserOptionsEqual(current: UserOption[], next: UserOption[]): boolean {
    if (current.length !== next.length) return false;
    return current.every((item, index) => {
      const target = next[index];
      return (
        item.userId === target.userId &&
        item.displayName === target.displayName &&
        item.code === target.code &&
        item.name === target.name
      );
    });
  }

  private areStringArraysEqual(current: string[], next: string[]): boolean {
    if (current.length !== next.length) return false;
    return current.every((value, index) => value === next[index]);
  }

  private getGenderLabel(record: RecordData): string {
    const raw = record?.data?.sex;
    const normalized = typeof raw === 'string' ? raw.toLowerCase().trim() : '';
    if (normalized === 'male' || normalized === 'm' || normalized === '男性') return this.i18n.label.male;
    if (normalized === 'female' || normalized === 'f' || normalized === '女性') return this.i18n.label.female;
    return this.i18n.label.other;
  }

  private getAgeGroupLabel(record: RecordData): string {
    const raw = record?.data?.ageGroup;
    if (raw != null && raw !== '') {
      const trimmed = String(raw).trim();
      if (!trimmed) return this.unknownLabel;
      if (/\d/.test(trimmed) && !/[代歳才]/.test(trimmed)) return `${trimmed}歳`;
      return trimmed;
    }

    const ageValue = Number(record?.data?.age);
    if (!Number.isFinite(ageValue)) return this.unknownLabel;
    if (ageValue < 10) return this.underTenLabel;
    if (ageValue >= 100) return this.overHundredLabel;

    const decade = Math.floor(ageValue / 10) * 10;
    return `${decade}代`;
  }

  private getScoreLabel(record: RecordData): string {
    const score = this.normalizeScore(record);
    return score === 'UNKNOWN' ? this.unknownLabel : score;
  }

  private normalizeScore(record: RecordData): ScoreLevel {
    const rawScore = record?.data?.score;
    if (rawScore == null || rawScore === '') return 'UNKNOWN';

    const normalized = String(rawScore).trim().toUpperCase();
    if (SCORE_LEVELS.includes(normalized as 'A' | 'B' | 'C')) {
      return normalized as ScoreLevel;
    }

    const numericScore = Number(rawScore);
    if (Number.isFinite(numericScore)) {
      const index = Math.trunc(numericScore) - 1;
      if (index >= 0 && index < SCORE_LEVELS.length) {
        return SCORE_LEVELS[index] as ScoreLevel;
      }
    }

    return 'UNKNOWN';
  }

  private countBy(
    records: RecordData[],
    keyBuilder: (r: RecordData) => string,
  ): ChartEntry[] {
    const counter = new Map<string, number>();
    for (const r of records) {
      const key = keyBuilder(r) || this.unknownLabel;
      counter.set(key, (counter.get(key) ?? 0) + 1);
    }
    return Array.from(counter.entries());
  }

  private sortByCustomOrder(entries: ChartEntry[], order: string[]): ChartEntry[] {
    const orderIndex = new Map(order.map((v, i) => [v, i]));
    return [...entries].sort((a, b) => {
      const iA = orderIndex.get(a[0]) ?? Number.MAX_SAFE_INTEGER;
      const iB = orderIndex.get(b[0]) ?? Number.MAX_SAFE_INTEGER;
      return iA !== iB ? iA - iB : b[1] - a[1];
    });
  }

  private sortByAgeGroupDesc(entries: ChartEntry[]): ChartEntry[] {
    const known = entries.filter(([l]) => l !== this.unknownLabel);
    const unknown = entries.filter(([l]) => l === this.unknownLabel);
    known.sort((a, b) => this.ageGroupSortKey(b[0]) - this.ageGroupSortKey(a[0]));
    return [...known, ...unknown];
  }

  private ageGroupSortKey(label: string): number {
    if (label === this.underTenLabel) return 0;
    if (label === this.overHundredLabel) return 1000;
    if (label === this.unknownLabel) return 9999;
    const match = label.match(/\d+/);
    return match ? Number(match[0]) : 9999;
  }

  private extractAgeRepresentative(record: RecordData): number | null {
    const label = this.getAgeGroupLabel(record);
    if (label === this.unknownLabel) return null;
    if (label === this.underTenLabel) return 5;
    if (label === this.overHundredLabel) return 100;
    const match = label.match(/\d+/);
    if (!match) return null;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toAgeGroupLabel(age: number): string {
    if (!Number.isFinite(age)) return this.unknownLabel;
    if (age < 10) return this.underTenLabel;
    if (age >= 100) return this.overHundredLabel;
    return `${Math.floor(age / 10) * 10}代`;
  }

  private getMedianAge(values: number[]): number | null {
    if (values.length === 0) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 1) {
      return sorted[mid];
    }
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }

  private toPercent(value: number, total: number): number {
    if (total <= 0) {
      return 0;
    }
    return Math.round((value / total) * 1000) / 10;
  }

  private normalizeDateRange(filters: TomassFilterState): TomassFilterState {
    if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      return { ...filters, startDate: filters.endDate, endDate: filters.startDate };
    }
    return filters;
  }
}
